import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import { merchantService, MerchantProfile } from '../services/merchantService';
import LoadingScreen from './LoadingScreen';
import { 
  Key, 
  Copy, 
  RefreshCw, 
  Globe, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Save,
  Building,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Loader,
  Edit,
  Mail,
  Phone,
  MapPin,
  Globe as WebIcon,
  Briefcase,
  Shield as LegalIcon,
  BookOpen
} from 'lucide-react';

const MerchantAccount: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const [profile, setProfile] = useState<MerchantProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingKYC, setIsEditingKYC] = useState(false);
  const [isEditingDocuments, setIsEditingDocuments] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<any>(null);

  const [callbackUrls, setCallbackUrls] = useState({
    success_url: '',
    failure_url: '',
    notification_url: ''
  });

  const [kycData, setKycData] = useState({
    institution_name: '',
    email: '',
    telephone: '',
    address: '',
    sector: '',
    legal_status: '',
    nif: '',
    rccm: '',
    idnat: '',
    website: ''
  });

  useEffect(() => {
    loadMerchantProfile();
  }, [user]);

  const loadMerchantProfile = async () => {
    try {
      setLoading(true);
      const profileData = await merchantService.getMerchantProfile();
      setProfile(profileData);
      setKycData({
        institution_name: profileData.institution.institution_name || '',
        email: profileData.institution.email || '',
        telephone: profileData.institution.telephone || '',
        address: profileData.institution.address || '',
        sector: profileData.institution.sector || '',
        legal_status: profileData.institution.legal_status || '',
        nif: profileData.institution.nif || '',
        rccm: profileData.institution.rccm || '',
        idnat: profileData.institution.idnat || '',
        website: profileData.institution.website || ''
      });
      
      const kycResponse = await fetchKycStatus();
      setKycStatus(kycResponse);
    } catch (error) {
      console.error('Error loading merchant profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKycStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944';
      const response = await fetch(`${API_URL}/merchants/kyc-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await response.json();
    } catch (error) {
      return { kyc_status: 'unknown', missing_documents: [] };
    }
  };

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRegenerateSecret = async () => {
    if (!confirm(t('merchant.confirmRegenerate'))) {
      return;
    }

    setIsRegenerating(true);
    try {
      const result = await merchantService.regenerateSecret();
      await loadMerchantProfile();
      alert(`New secret key generated: ${result.merchant_secrete}`);
    } catch (error) {
      alert(t('merchant.errorRegenerateSecret'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSaveCallbackUrls = async () => {
    setIsSaving(true);
    try {
      await merchantService.updateCallbackUrls(callbackUrls);
      alert(t('merchant.callbackUrlsUpdated'));
    } catch (error) {
      alert(t('merchant.errorUpdatingUrls'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveKYC = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944';
      
      const response = await fetch(`${API_URL}/merchants/update-kyc`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(kycData)
      });

      if (!response.ok) {
        throw new Error('Failed to update KYC information');
      }

      await loadMerchantProfile();
      setIsEditingKYC(false);
      setIsEditingDocuments(false);
      alert(t('merchant.kycUpdated'));
    } catch (error) {
      alert(t('merchant.errorUpdatingKyc'));
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'bg-green-100 text-green-800 border border-green-200';
      case 0: return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return t('status.active');
      case 0: return t('status.pending');
      default: return t('common.unavailable');
    }
  };

  const getKycStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getKycStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('merchant.verified');
      case 'pending': return t('merchant.pendingReview');
      case 'not_started': return t('merchant.notStarted');
      default: return 'Unknown';
    }
  };

  if (loading) {
    return <LoadingScreen fullscreen={false} message={t('account.loading') || 'Chargement...'} />;
  }

  if (!profile) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('merchant.noAccountFound')}</h3>
        <p className="text-gray-600">
          {t('merchant.contactAdmin')}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}

        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
          <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
          <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-white">{t('merchant.title')}</h1>
              <p className="text-slate-400 mt-1 text-sm">{t('merchant.subtitle')}</p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              {/* KYC Status Badge */}
              {kycStatus && (
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/20 shadow-sm">
                  {getKycStatusIcon(kycStatus.kyc_status)}
                  <div>
                    <div className="text-xs font-medium text-slate-300">{t('merchant.kycStatus')}</div>
                    <div className={`text-xs font-semibold ${
                      kycStatus.kyc_status === 'completed' ? 'text-emerald-400' :
                      kycStatus.kyc_status === 'pending' ? 'text-amber-400' : 'text-rose-400'
                    }`}>
                      {getKycStatusText(kycStatus.kyc_status)}
                    </div>
                  </div>
                </div>
              )}

              {/* Account Status */}
              <div className={`px-3 py-2 rounded-lg text-xs font-semibold border ${
                profile.merchant.status === 1 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                profile.merchant.status === 0 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
                'bg-rose-500/20 text-rose-300 border-rose-500/30'
              }`}>
                {getStatusText(profile.merchant.status)}
              </div>
            </div>
          </div>
        </div>

        {/* KYC Alert */}
        {kycStatus && kycStatus.kyc_status !== 'completed' && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-start">
              <AlertTriangle className="h-6 w-6 text-yellow-600 mt-0.5 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                  {t('merchant.completeKyc')}
                </h4>
                <p className="text-yellow-700 mb-3">
                  {kycStatus.missing_documents.length > 0
                    ? `${t('merchant.missingDocs')} ${kycStatus.missing_documents.join(', ')}`
                    : t('merchant.completeKycNote')
                  }
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setIsEditingKYC(true)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    {t('merchant.updateCompanyInfo')}
                  </button>
                  <button
                    onClick={() => setIsEditingDocuments(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    {t('merchant.updateDocs')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left Column - API Credentials & Callbacks */}
          <div className="xl:col-span-2 space-y-8">
            {/* API Credentials Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-6 py-4">
                <div className="flex items-center">
                  <Key className="h-6 w-6 text-white mr-3" />
                  <h2 className="text-xl font-bold text-white">{t('merchant.apiCredentials')}</h2>
                </div>
                <p className="text-green-100 text-sm mt-1">{t('merchant.apiDesc')}</p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* API Base URL */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-900">{t('merchant.apiBaseUrl')}</h3>
                      <p className="text-emerald-700 text-sm">https://paydrc.gofreshbakery.net/api/v5/</p>
                    </div>
                    <button
                      onClick={() => handleCopy('https://paydrc.gofreshbakery.net/api/v5/', 'api_url')}
                      className="p-2 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors duration-200"
                    >
                      {copySuccess === 'api_url' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Merchant ID */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.merchantId')}</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={profile.merchant.merchant_id}
                        readOnly
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 font-mono text-sm focus:outline-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      />
                      <button
                        onClick={() => handleCopy(profile.merchant.merchant_id, 'merchant_id')}
                        className="p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors duration-200 border border-gray-300"
                      >
                        {copySuccess === 'merchant_id' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Merchant Code */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.merchantCode')}</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={profile.merchant.merchant_code}
                        readOnly
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 font-mono text-sm focus:outline-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                      />
                      <button
                        onClick={() => handleCopy(profile.merchant.merchant_code, 'merchant_code')}
                        className="p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors duration-200 border border-gray-300"
                      >
                        {copySuccess === 'merchant_code' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Merchant Secret */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-700">Merchant Secret</label>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setShowSecret(!showSecret)}
                        className="flex items-center text-sm text-gray-600 hover:text-emerald-600 transition-colors duration-200"
                      >
                        {showSecret ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                        {showSecret ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={handleRegenerateSecret}
                        disabled={isRegenerating}
                        className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-all duration-200 text-sm font-medium"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
                        {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type={showSecret ? 'text' : 'password'}
                      value={profile.merchant.merchant_secrete}
                      readOnly
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 font-mono text-sm focus:outline-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                    <button
                      onClick={() => handleCopy(profile.merchant.merchant_secrete, 'merchant_secret')}
                      className="p-3 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors duration-200 border border-gray-300"
                    >
                      {copySuccess === 'merchant_secret' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Last updated: {new Date(profile.merchant.updated_at).toLocaleString('en-US')}
                  </p>
                </div>

                {/* API Request Example */}
                <div className="bg-gray-900 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-green-400">{t('merchant.apiRequestExample')}</h4>
                    <button
                      onClick={() => handleCopy(JSON.stringify({
                        merchant_id: profile.merchant.merchant_id,
                        merchant_secrete: profile.merchant.merchant_secrete,
                        amount: "xxxxxx",
                        currency: "xxxxxx",
                        action: "xxxxxx",
                        customer_number: "xxxxxx",
                        firstname: profile.user.firstname,
                        lastname: profile.user.lastname,
                        "e-mail": profile.user.email,
                        reference: "xxxxxx",
                        method: "xxxxxx",
                        callback_url: "xxxxxx"
                      }, null, 2), 'api_example')}
                      className="text-green-400 hover:text-green-300 text-sm"
                    >
                      {t('merchant.copyExample')}
                    </button>
                  </div>
                  <pre className="text-green-400 text-xs overflow-x-auto">
{`POST https://paydrc.gofreshbakery.net/api/v5/
{
  "merchant_id": "${profile.merchant.merchant_id}",
  "merchant_secrete": "******************",
  "amount": "******************",
  "currency": "******************",
  "action": "******************",
  "customer_number": "******************",
  "firstname": "${profile.user.firstname}",
  "lastname": "${profile.user.lastname}",
  "e-mail": "${profile.user.email}",
  "reference": "******************",
  "method": "******************",
  "callback_url": "******************"
}`}
                  </pre>
                </div>

                {/* Security Notice */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-red-800 mb-2">{t('merchant.securityNotice')}</h4>
                      <ul className="text-xs text-red-700 space-y-1">
                        <li>• {t('merchant.securityTip1')}</li>
                        <li>• {t('merchant.securityTip2')}</li>
                        <li>• {t('merchant.securityTip3')}</li>
                        <li>• {t('merchant.securityTip4')}</li>
                        <li>• {t('merchant.securityTip5')}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Callback URLs Card */}
            {/* <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-[#000065] to-[#1e1b4b] px-6 py-4">
                <div className="flex items-center">
                  <Globe className="h-6 w-6 text-white mr-3" />
                  <h2 className="text-xl font-bold text-white">{t('merchant.callbackUrls')}</h2>
                </div>
                <p className="text-blue-100 text-sm mt-1">{t('merchant.callbackDesc')}</p>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.successUrl')}</label>
                    <input
                      type="url"
                      value={callbackUrls.success_url}
                      onChange={(e) => setCallbackUrls({...callbackUrls, success_url: e.target.value})}
                      placeholder="https://yoursite.com/payment/success"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200"
                    />
                    <p className="text-xs text-gray-500 mt-1">Called after successful transactions</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.failureUrl')}</label>
                    <input
                      type="url"
                      value={callbackUrls.failure_url}
                      onChange={(e) => setCallbackUrls({...callbackUrls, failure_url: e.target.value})}
                      placeholder="https://yoursite.com/payment/failure"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200"
                    />
                    <p className="text-xs text-gray-500 mt-1">Called after failed transactions</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.webhookUrl')}</label>
                    <input
                      type="url"
                      value={callbackUrls.notification_url}
                      onChange={(e) => setCallbackUrls({...callbackUrls, notification_url: e.target.value})}
                      placeholder="https://yoursite.com/api/webhook/paydrc"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200"
                    />
                    <p className="text-xs text-gray-500 mt-1">For real-time transaction status updates</p>
                  </div>
                </div>

                <button
                  onClick={handleSaveCallbackUrls}
                  disabled={isSaving}
                  className="w-full bg-gradient-to-r from-[#63d400] to-[#4CAF50] text-white py-3 px-6 rounded-xl hover:from-[#58c400] hover:to-[#45a049] disabled:opacity-50 transition-all duration-200 font-semibold flex items-center justify-center"
                >
                  <Save className={`h-5 w-5 mr-2 ${isSaving ? 'animate-pulse' : ''}`} />
                  {isSaving ? t('merchant.savingUrls') : t('merchant.saveCallbackUrls')}
                </button>
              </div>
            </div> */}
          </div>

          {/* Right Column - Profile Information */}
          <div className="space-y-8">
            {/* Company Information Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Building className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-xl font-bold text-white">{t('merchant.companyInfo')}</h2>
                  </div>
                  <button
                    onClick={() => setIsEditingKYC(true)}
                    className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors duration-200"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {!isEditingKYC ? (
                  <>
                    <div className="flex items-center space-x-3">
                      <Building className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{profile.institution.institution_name}</div>
                        <div className="text-xs text-gray-500">{t('merchant.businessName')}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{profile.institution.email}</div>
                        <div className="text-xs text-gray-500">{t('profile.email')}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{profile.institution.telephone}</div>
                        <div className="text-xs text-gray-500">{t('profile.phone')}</div>
                      </div>
                    </div>
                    
                    {profile.institution.address && (
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{profile.institution.address}</div>
                          <div className="text-xs text-gray-500">{t('profile.address')}</div>
                        </div>
                      </div>
                    )}
                    
                    {profile.institution.website && (
                      <div className="flex items-center space-x-3">
                        <WebIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{profile.institution.website}</div>
                          <div className="text-xs text-gray-500">{t('profile.website')}</div>
                        </div>
                      </div>
                    )}
                    
                    {profile.institution.sector && (
                      <div className="flex items-center space-x-3">
                        <Briefcase className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{profile.institution.sector}</div>
                          <div className="text-xs text-gray-500">{t('merchant.industrySector')}</div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">{t('merchant.editCompanyInfo')}</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.companyName')} *</label>
                        <input
                          type="text"
                          value={kycData.institution_name}
                          onChange={(e) => setKycData({...kycData, institution_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.email')} *</label>
                        <input
                          type="email"
                          value={kycData.email}
                          onChange={(e) => setKycData({...kycData, email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.phone')} *</label>
                        <input
                          type="tel"
                          value={kycData.telephone}
                          onChange={(e) => setKycData({...kycData, telephone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.address')}</label>
                        <input
                          type="text"
                          value={kycData.address}
                          onChange={(e) => setKycData({...kycData, address: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.industrySector')}</label>
                        <input
                          type="text"
                          value={kycData.sector}
                          onChange={(e) => setKycData({...kycData, sector: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('profile.website')}</label>
                        <input
                          type="url"
                          value={kycData.website}
                          onChange={(e) => setKycData({...kycData, website: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleSaveKYC}
                        disabled={isSaving}
                        className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors duration-200"
                      >
                        {isSaving ? t('common.saving') : t('common.saveChanges')}
                      </button>
                      <button
                        onClick={() => setIsEditingKYC(false)}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* KYC Documents Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FileText className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-xl font-bold text-white">{t('merchant.kycDocs')}</h2>
                  </div>
                  <button
                    onClick={() => setIsEditingDocuments(true)}
                    className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors duration-200"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {!isEditingDocuments ? (
                  <>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{t('merchant.taxId')}</div>
                          <div className="text-xs text-gray-500">{t('merchant.taxIdNumber')}</div>
                          {profile.institution.nif && (
                            <div className="text-xs text-green-600 font-medium mt-1">{profile.institution.nif}</div>
                          )}
                        </div>
                        {profile.institution.nif ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{t('merchant.businessRegister')}</div>
                          <div className="text-xs text-gray-500">{t('merchant.commercialRegistry')}</div>
                          {profile.institution.rccm && (
                            <div className="text-xs text-green-600 font-medium mt-1">{profile.institution.rccm}</div>
                          )}
                        </div>
                        {profile.institution.rccm ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{t('merchant.nationalId')}</div>
                          <div className="text-xs text-gray-500">{t('merchant.nationalIdentification')}</div>
                          {profile.institution.idnat && (
                            <div className="text-xs text-green-600 font-medium mt-1">{profile.institution.idnat}</div>
                          )}
                        </div>
                        {profile.institution.idnat ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{t('merchant.legalStatus')}</div>
                          <div className="text-xs text-gray-500">{t('merchant.legalStructure')}</div>
                          {profile.institution.legal_status && (
                            <div className="text-xs text-green-600 font-medium mt-1">{profile.institution.legal_status}</div>
                          )}
                        </div>
                        {profile.institution.legal_status ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setIsEditingDocuments(true)}
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all duration-200 font-semibold"
                    >
                      {t('merchant.updateDocuments')}
                    </button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">{t('merchant.editKycDocs')}</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.taxId')} *</label>
                        <input
                          type="text"
                          value={kycData.nif}
                          onChange={(e) => setKycData({...kycData, nif: e.target.value})}
                          placeholder={t('merchant.enterTaxId')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('merchant.requiredBusinessVerification')}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.businessRegister')} *</label>
                        <input
                          type="text"
                          value={kycData.rccm}
                          onChange={(e) => setKycData({...kycData, rccm: e.target.value})}
                          placeholder={t('merchant.enterRccm')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('merchant.requiredBusinessRegistration')}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.nationalId')} *</label>
                        <input
                          type="text"
                          value={kycData.idnat}
                          onChange={(e) => setKycData({...kycData, idnat: e.target.value})}
                          placeholder={t('merchant.enterIdnat')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">{t('merchant.requiredIdentityVerification')}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('merchant.legalStatus')} *</label>
                        <select
                          value={kycData.legal_status}
                          onChange={(e) => setKycData({...kycData, legal_status: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                          required
                        >
                          <option value="">{t('merchant.selectLegalStatus')}</option>
                          <option value="SARL">SARL (Limited Liability Company)</option>
                          <option value="SA">SA (Public Limited Company)</option>
                          <option value="SNC">SNC (General Partnership)</option>
                          <option value="SCS">SCS (Limited Partnership)</option>
                          <option value="EI">EI (Individual Enterprise)</option>
                          <option value="Other">Other</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">{t('merchant.selectLegalStructure')}</p>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleSaveKYC}
                        disabled={isSaving}
                        className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors duration-200"
                      >
                        {isSaving ? t('common.saving') : t('merchant.saveDocs')}
                      </button>
                      <button
                        onClick={() => setIsEditingDocuments(false)}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
                <div className="flex items-center">
                  <User className="h-6 w-6 text-white mr-3" />
                  <h2 className="text-xl font-bold text-white">{t('merchant.primaryContact')}</h2>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {profile.user.firstname} {profile.user.lastname}
                    </div>
                    <div className="text-xs text-gray-500">{t('merchant.contactPerson')}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{profile.user.email}</div>
                    <div className="text-xs text-gray-500">{t('merchant.emailAddress')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default MerchantAccount;